import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { getContext } from '../../services/api/core';
import { ActivityLog } from '@repo/shared-types';
import { activityLogSchema } from '@repo/shared-types/common/activitySchemas';
import { logger, validateEntityArray } from '@repo/shared-utils';

export const useActivityLogs = () => {
  return useQuery({
    queryKey: ['activity', 'logs'],
    queryFn: async () => {
      const data = await api.activity.logs.list();
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(activityLogSchema, data);
      if (!validation.valid) {
        logger.error('Invalid activity logs query response:', validation.errors);
        throw new Error('Invalid activity logs data received from API');
      }
      return validation.data! as ActivityLog[];
    },
    staleTime: 1000 * 60 * 1, // 1 minute - activity logs change frequently
  });
};

export interface AdminOrgActivityLogFilters {
  operatingOrganizationId: string;
  actorId?: string;
  targetEntityType?: string;
}

/** Org-scoped audit timeline (GET /admin/activity-logs). */
export const useAdminOrgActivityLogs = (
  filters: AdminOrgActivityLogFilters,
  queryOptions?: { enabled?: boolean },
) => {
  const orgId = filters.operatingOrganizationId.trim();
  const actorId = filters.actorId?.trim() ?? '';
  const targetEntityType = filters.targetEntityType?.trim() ?? '';
  const enabled =
    queryOptions?.enabled === undefined ? orgId.length > 0 : queryOptions.enabled && orgId.length > 0;

  return useQuery({
    queryKey: ['admin-activity-logs', orgId, actorId, targetEntityType] as const,
    enabled,
    queryFn: async () => {
      const ctx = getContext();
      const data = await api.activity.admin.listOrgAudit(
        {
          operatingOrganizationId: orgId,
          ...(actorId ? { actorId } : {}),
          ...(targetEntityType ? { targetEntityType } : {}),
        },
        ctx,
      );
      const validation = validateEntityArray(activityLogSchema, data);
      if (!validation.valid) {
        logger.error('Invalid admin activity logs query response:', validation.errors);
        throw new Error('Invalid admin activity logs data received from API');
      }
      return validation.data! as ActivityLog[];
    },
  });
};

export const useAddActivityLog = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (log: Omit<ActivityLog, 'id' | 'timestamp'>) => api.activity.logs.add(log),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activity', 'logs'] });
    }
  });
};

